<main class="bg-black text-white min-h-screen overflow-x-hidden">

  <!-- Hero -->
  <section class="relative min-h-[65vh] flex items-center pt-24 pb-14 overflow-hidden page-hero-gradient">
    <div class="absolute -top-48 right-0 w-[700px] h-[700px] bg-varroc-accent/5 rounded-full blur-3xl"></div>
    <div class="absolute inset-0 opacity-[0.025]" style="background-image: linear-gradient(rgba(0,170,230,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(0,170,230,0.5) 1px, transparent 1px); background-size: 60px 60px;"></div>

    <div class="relative z-10 max-w-7xl mx-auto px-6 w-full">
      <div class="max-w-3xl">
        <a routerLink="/products/connectiq" class="back-nav" data-aos="fade-down">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" class="w-4 h-4 svg-stroke-gradient"><polyline points="15 18 9 12 15 6"></polyline></svg>
          Back
        </a>
        <h1 class="hero-col font-black mb-5" data-aos="fade-up" data-aos-delay="100">
          W4IQ <span class="text-varroc-accent">Streaming &amp; Data</span> Layer
        </h1>
        <p class="text-varroc-text-gray text-lg leading-relaxed mb-8 max-w-2xl" data-aos="fade-up" data-aos-delay="200">
          W4IQ standardizes IoT packets as Geobits in a Who, Where, When, What model. It serves real-time and historical analytics through a dual-tier hot/cold architecture backed by Kafka-powered streaming.
        </p>
        <div class="flex flex-wrap gap-4" data-aos="fade-up" data-aos-delay="300">
          <a routerLink="/contact-us" class="bg-varroc-accent text-white font-bold px-8 py-4 rounded-md hover:bg-varroc-accent-hover hover:shadow-neon transition-all">
            Request Demo
          </a>
         
        </div>
      </div>
    </div>
  </section>

  <!-- Sticky Nav -->
  <nav class="sticky-product-nav">
    <div class="max-w-7xl mx-auto px-6">
      <div class="flex overflow-x-auto">
        <a *ngFor="let s of sections" (click)="scrollTo(s.id)" [class.active]="activeSection === s.id" class="cursor-pointer flex-shrink-0">
          <i class='bx text-base' [class]="s.icon"></i> {{ s.label }}
        </a>
      </div>
    </div>
  </nav>

  <!-- Content -->
  <div class="max-w-7xl mx-auto px-6 py-20 space-y-28">

    <!-- Overview -->
    <section id="overview" class="section-anchor">
      <div class="grid md:grid-cols-2 gap-12 items-start">
        <div data-aos="fade-right">
          <span class="text-varroc-accent text-sm font-bold uppercase tracking-widest">What Is W4IQ</span>
          <h2 class="text-4xl font-black mt-3 mb-5">IoT Data in a Who, Where, When, What Model</h2>
          <p class="text-varroc-text-gray leading-relaxed mb-5">W4IQ is the data layer of ConnectIQ that normalizes all incoming IoT packets into a unified Geobit format — structured around four dimensions: Who (identity), Where (location), When (timestamp), and What (payload).</p>
          <p class="text-varroc-text-gray leading-relaxed">This standardized representation powers both real-time operational dashboards and deep historical analytics, ensuring data consistency across every downstream consumer in the platform.</p>
        </div>
        <div class="feature-card" data-aos="fade-left">
          <h3 class="text-white font-bold text-lg mb-5">The Geobit Model</h3>
          <div class="space-y-3">
            <div class="flex items-center gap-4 p-3 border border-varroc-border rounded-xl">
              <div class="w-10 h-10 bg-varroc-accent/15 rounded-lg flex items-center justify-center flex-shrink-0">
                <span class="text-varroc-accent font-black text-sm">W1</span>
              </div>
              <div>
                <div class="text-white font-semibold text-sm">Who</div>
                <div class="text-varroc-text-gray text-xs">Device & user identity</div>
              </div>
            </div>
            <div class="flex items-center gap-4 p-3 border border-varroc-border rounded-xl">
              <div class="w-10 h-10 bg-varroc-accent/15 rounded-lg flex items-center justify-center flex-shrink-0">
                <span class="text-varroc-accent font-black text-sm">W2</span>
              </div>
              <div>
                <div class="text-white font-semibold text-sm">Where</div>
                <div class="text-varroc-text-gray text-xs">GPS coordinates & geofence context</div>
              </div>
            </div>
            <div class="flex items-center gap-4 p-3 border border-varroc-border rounded-xl">
              <div class="w-10 h-10 bg-varroc-accent/15 rounded-lg flex items-center justify-center flex-shrink-0">
                <span class="text-varroc-accent font-black text-sm">W3</span>
              </div>
              <div>
                <div class="text-white font-semibold text-sm">When</div>
                <div class="text-varroc-text-gray text-xs">Precise event timestamps</div>
              </div>
            </div>
            <div class="flex items-center gap-4 p-3 border border-varroc-border rounded-xl">
              <div class="w-10 h-10 bg-varroc-accent/15 rounded-lg flex items-center justify-center flex-shrink-0">
                <span class="text-varroc-accent font-black text-sm">W4</span>
              </div>
              <div>
                <div class="text-white font-semibold text-sm">What</div>
                <div class="text-varroc-text-gray text-xs">Telemetry payload & sensor data</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <div class="accent-divider"></div>

    <!-- Streaming -->
    <section id="streaming" class="section-anchor">
      <div class="mb-12" data-aos="fade-up">
        <span class="text-varroc-accent text-sm font-bold uppercase tracking-widest">Kafka-Backed Streaming</span>
        <h2 class="text-4xl font-black mt-2 mb-4">Streaming Capabilities</h2>
        <p class="text-varroc-text-gray max-w-2xl mr-auto">A fault-tolerant, high-throughput event backbone that decouples producers from consumers for scalable, reliable data flow.</p>
      </div>
      <div class="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
        <div *ngFor="let cap of streamingCapabilities; let i = index" class="feature-card flex items-start gap-4" data-aos="fade-up" [attr.data-aos-delay]="i * 60">
          <div class="w-8 h-8 bg-varroc-accent/15 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
            <i class='bx bx-broadcast icon-gradient text-base'></i>
          </div>
          <p class="text-varroc-text-gray text-sm leading-relaxed">{{ cap }}</p>
        </div>
      </div>
    </section>

    <div class="accent-divider"></div>

    <!-- Data Layers -->
    <section id="data-layers" class="section-anchor">
      <div class="mb-12" data-aos="fade-up">
        <span class="text-varroc-accent text-sm font-bold uppercase tracking-widest">Dual-Tier Architecture</span>
        <h2 class="text-4xl font-black mt-2 mb-4">Hot &amp; Cold Data Layers</h2>
        <p class="text-varroc-text-gray max-w-2xl mr-auto">Two complementary storage tiers that balance real-time responsiveness with deep historical depth.</p>
      </div>
      <div class="grid md:grid-cols-2 gap-8">
        <div *ngFor="let layer of dataLayers" class="feature-card" data-aos="fade-up">
          <div class="flex items-center gap-3 mb-4">
            <div class="w-12 h-12 bg-varroc-accent/15 rounded-xl flex items-center justify-center">
              <i class='bx icon-gradient text-2xl' [class]="layer.icon"></i>
            </div>
            <div>
              <div class="text-white font-bold text-lg">{{ layer.title }}</div>
              <div class="text-varroc-accent text-xs font-semibold">{{ layer.badge }}</div>
            </div>
          </div>
          <p class="text-varroc-text-gray text-sm leading-relaxed mb-4">{{ layer.desc }}</p>
          <div class="space-y-2">
            <div *ngFor="let m of layer.metrics" class="flex items-center gap-2 text-varroc-text-gray text-xs">
              <i class='bx bx-check icon-gradient'></i>
              <span>{{ m }}</span>
            </div>
          </div>
        </div>
      </div>
    </section>

    <div class="accent-divider"></div>

    <!-- Data Flow -->
    <section id="flow" class="section-anchor">
      <div class="mb-12" data-aos="fade-up">
        <span class="text-varroc-accent text-sm font-bold uppercase tracking-widest">End-to-End Pipeline</span>
        <h2 class="text-4xl font-black mt-2 mb-4">From Event to Insight</h2>
        <p class="text-varroc-text-gray max-w-2xl mr-auto">A four-stage pipeline from device event ingestion to analytics-ready data serving.</p>
      </div>
      <div class="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div *ngFor="let step of streamingFlow; let i = index" class="feature-card text-center relative" data-aos="fade-up" [attr.data-aos-delay]="i * 80">
          <div class="text-varroc-accent font-black text-3xl mb-3 opacity-40">{{ step.step }}</div>
          <h3 class="text-white font-bold text-lg mb-2">{{ step.label }}</h3>
          <p class="text-varroc-text-gray text-sm">{{ step.desc }}</p>
        </div>
      </div>
    </section>

  </div>
</main>
